import BaseObject from "@shared/BaseObject.js";
import {createDialogLazy} from "@/utils/helpers-external";
import {GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} from "@/core/js/model/SharedConstants";
import ReviewPromptManager from "@/core/modules/review/ReviewPromptManager";
import MeaningfulUsageTracker from "@/core/modules/review/MeaningfulUsageTracker";
import {ReviewPromptCompletionReasonEnum} from "@/core/modules/review/ReviewPromptCompletionReasonEnum";
import {
	GENOGRAM_MEANINGFUL_USAGE_CUMULATIVE_MS,
	GENOGRAM_MEANINGFUL_USAGE_IDLE_GAP_MS,
	GENOGRAM_MEANINGFUL_USAGE_STREAK_MS,
	GENOGRAM_PUBLIC_REVIEW_URL,
	GENOGRAM_REVIEW_BLOCK_AFTER_PAYMENT_MS,
	GENOGRAM_REVIEW_MAX_DISMISS_COUNT,
	GENOGRAM_REVIEW_SCOPE_KEY,
	GENOGRAM_REVIEW_SNOOZE_MS,
} from "@/tools/GenogramTool/review/GenogramReviewConstants";

let ReviewExperienceRatingDialog = () => import("@/core/modules/review/dialogs/ReviewExperienceRatingDialog.vue");
let ReviewPublicAskDialog = () => import("@/core/modules/review/dialogs/ReviewPublicAskDialog.vue");
let ReviewPublicFollowupDialog = () => import("@/core/modules/review/dialogs/ReviewPublicFollowupDialog.vue");
let FeedbackDialog = () => import("@/tools/FeedbackGather/FeedbackDialog.vue");

let reviewExperienceRatingDialog = createDialogLazy(ReviewExperienceRatingDialog)
let reviewPublicAskDialog = createDialogLazy(ReviewPublicAskDialog)
let reviewPublicFollowupDialog = createDialogLazy(ReviewPublicFollowupDialog)
let feedbackDialog = createDialogLazy(FeedbackDialog)

export default class GenogramReviewFlowManager extends BaseObject {

	constructor(component) {
		super()
		this.component = component
		this.reviewPromptManager = new ReviewPromptManager()
		this.meaningfulUsageTracker = new MeaningfulUsageTracker({
			streakThresholdMs: GENOGRAM_MEANINGFUL_USAGE_STREAK_MS,
			cumulativeThresholdMs: GENOGRAM_MEANINGFUL_USAGE_CUMULATIVE_MS,
			idleGapMs: GENOGRAM_MEANINGFUL_USAGE_IDLE_GAP_MS,
		})
	}

	getMeaningfulUsageTracker() {
		return this.meaningfulUsageTracker
	}

	async markPaymentCompleted() {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			this.logDiagnostic("markPaymentCompleted skipped: missing userProfile")
			return false
		}

		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		let blockUntil = Date.now() + GENOGRAM_REVIEW_BLOCK_AFTER_PAYMENT_MS
		this.reviewPromptManager.markBlocked(reviewPromptState, blockUntil)
		this.logDiagnostic("payment completed -> block applied", {
			blockUntil,
			reviewPromptState,
		})
		await this.persistUserProfile(userProfile)
		return true
	}

	async registerMeaningfulUsage(itemId, timestampMs = Date.now()) {
		let userProfile = this.getUserProfile()
		if(!userProfile || !itemId){
			this.logDiagnostic("registerMeaningfulUsage skipped", {
				hasUserProfile: !!userProfile,
				itemId,
				timestampMs,
			})
			return false
		}
		let reviewPromptState = this.reviewPromptManager.getState(userProfile)

		let existingMeaningfulUsageAtMap = this.meaningfulUsageTracker.getMeaningfulUsageMap(
			reviewPromptState,
			GENOGRAM_REVIEW_SCOPE_KEY
		)
		let wasAlreadyTracked = !!existingMeaningfulUsageAtMap[itemId]
		let meaningfulUsageAtMap = this.meaningfulUsageTracker.markMeaningfulUsage(
			reviewPromptState,
			GENOGRAM_REVIEW_SCOPE_KEY,
			itemId,
			timestampMs
		)
		let wasAddedNow = !wasAlreadyTracked && meaningfulUsageAtMap[itemId] === timestampMs
		if(!wasAddedNow){
			this.logDiagnostic("meaningful usage already tracked", {
				itemId,
				timestampMs,
				meaningfulUsageAtMap,
			})
			return false
		}

		this.reviewPromptManager.markEligible(reviewPromptState, timestampMs)
		this.logDiagnostic("meaningful usage registered -> eligible", {
			itemId,
			timestampMs,
			meaningfulUsageCount: this.getMeaningfulUsageCount(),
			reviewPromptState,
		})
		await this.persistUserProfile(userProfile)
		return true
	}

	getMeaningfulUsageCount() {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			return 0
		}
		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		return this.meaningfulUsageTracker.getMeaningfulUsageCount(
			reviewPromptState,
			GENOGRAM_REVIEW_SCOPE_KEY
		)
	}

	async maybeRunPromptFlow(trigger) {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			this.logDiagnostic("maybeRunPromptFlow skipped: missing userProfile", {trigger})
			return false
		}

		let now = Date.now()
		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		let canAsk = this.reviewPromptManager.canAsk(reviewPromptState, now, {
			maxDismissCount: GENOGRAM_REVIEW_MAX_DISMISS_COUNT,
		})
		this.logDiagnostic("prompt gate evaluated", {
			trigger,
			now,
			canAsk,
			reviewPromptState,
			isCompleted: this.reviewPromptManager.isCompleted(reviewPromptState),
			isBlocked: this.reviewPromptManager.isBlocked(reviewPromptState, now),
			isSnoozed: this.reviewPromptManager.isSnoozed(reviewPromptState, now),
			isEligibleNow: this.reviewPromptManager.isEligibleNow(reviewPromptState, now),
			maxDismissCount: GENOGRAM_REVIEW_MAX_DISMISS_COUNT,
		})
		if(!canAsk){
			return false
		}

		this.reviewPromptManager.markShown(reviewPromptState, now)
		this.logDiagnostic("prompt shown", {
			trigger,
			reviewPromptState,
		})
		await this.persistUserProfile(userProfile)
		this.trackEvent("Genogram review prompt shown", {trigger})

		let experienceResult = await reviewExperienceRatingDialog({
			title: "How has your experience with EasyGenogram been so far?",
			message: "Please rate your experience from 1 to 5.",
		})

		return this.handleExperienceResult(experienceResult, trigger)
	}

	async handleExperienceResult(experienceResult, trigger) {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			return false
		}

		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		let now = Date.now()
		let didHandle = false

		if(experienceResult?.action === "rated"){
			didHandle = true
			this.logDiagnostic("experience rated", {
				trigger,
				score: experienceResult.score,
			})
			this.trackEvent("Genogram review rating selected", {
				trigger,
				score: experienceResult.score,
				meaningful_genogram_count: this.getMeaningfulUsageCount(),
			})
			if(experienceResult.score === 5){
				await this.showPublicReviewAsk(trigger)
			} else {
				await this.handleLowExperienceScore(experienceResult.score, trigger)
			}
		}

		if(experienceResult?.action === "later"){
			didHandle = true
			this.reviewPromptManager.markSnoozed(reviewPromptState, now + GENOGRAM_REVIEW_SNOOZE_MS)
			this.logDiagnostic("experience prompt snoozed", {
				trigger,
				snoozedUntil: reviewPromptState.snoozedUntil,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram review prompt snoozed", {trigger})
		}

		if(!didHandle){
			this.reviewPromptManager.markDismissed(reviewPromptState, {
				now,
				snoozedUntil: now + GENOGRAM_REVIEW_SNOOZE_MS,
				increaseDismissCount: true,
			})
			this.logDiagnostic("experience prompt dismissed", {
				trigger,
				reviewPromptState,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram review prompt dismissed", {trigger})
		}

		return didHandle
	}

	async showPublicReviewAsk(trigger) {
		let askResult = await reviewPublicAskDialog({
			reviewUrl: GENOGRAM_PUBLIC_REVIEW_URL,
			title: "Would you be open to sharing an honest review on G2?",
			message: "G2 may ask you to verify your identity. Your review helps other people decide whether EasyGenogram is worth trying.",
			primaryButtonText: "Open Review",
		})
		return this.handlePublicAskResult(askResult, trigger)
	}

	async handlePublicAskResult(askResult, trigger) {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			return false
		}

		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		let now = Date.now()
		let didHandle = false

		if(askResult?.action === "open_review"){
			didHandle = true
			this.logDiagnostic("public review opened", {trigger})
			this.trackEvent("Genogram public review opened", {trigger})
			let followupResult = await reviewPublicFollowupDialog({
				title: "Thanks for opening the review",
				message: "If you were able to finish the review, let us know here.",
			})
			await this.handlePublicFollowupResult(followupResult, trigger)
		}

		if(askResult?.action === "later"){
			didHandle = true
			this.reviewPromptManager.markSnoozed(reviewPromptState, now + GENOGRAM_REVIEW_SNOOZE_MS)
			this.logDiagnostic("public review deferred", {
				trigger,
				snoozedUntil: reviewPromptState.snoozedUntil,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram public review deferred", {trigger})
		}

		if(askResult?.action === "never"){
			didHandle = true
			this.reviewPromptManager.markCompleted(reviewPromptState, {
				now,
				reason: ReviewPromptCompletionReasonEnum.NEVER_ASK_AGAIN,
			})
			this.logDiagnostic("public review never ask again", {
				trigger,
				reviewPromptState,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram public review stopped", {trigger})
		}

		if(!didHandle){
			this.reviewPromptManager.markDismissed(reviewPromptState, {
				now,
				snoozedUntil: now + GENOGRAM_REVIEW_SNOOZE_MS,
				increaseDismissCount: true,
			})
			this.logDiagnostic("public review dialog dismissed", {
				trigger,
				reviewPromptState,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram public review dialog dismissed", {trigger})
		}

		return didHandle
	}

	async handlePublicFollowupResult(followupResult, trigger) {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			return false
		}

		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		let now = Date.now()
		let didHandle = false

		if(followupResult?.action === "completed"){
			didHandle = true
			this.reviewPromptManager.markCompleted(reviewPromptState, {
				now,
				reason: ReviewPromptCompletionReasonEnum.PUBLIC_REVIEW_COMPLETED,
			})
			this.logDiagnostic("public review completed", {
				trigger,
				reviewPromptState,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram public review completed", {trigger})
		}

		if(followupResult?.action === "later"){
			didHandle = true
			this.reviewPromptManager.markSnoozed(reviewPromptState, now + GENOGRAM_REVIEW_SNOOZE_MS)
			this.logDiagnostic("public review followup deferred", {
				trigger,
				snoozedUntil: reviewPromptState.snoozedUntil,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram public review followup deferred", {trigger})
		}

		if(!didHandle){
			this.reviewPromptManager.markDismissed(reviewPromptState, {
				now,
				snoozedUntil: now + GENOGRAM_REVIEW_SNOOZE_MS,
				increaseDismissCount: true,
			})
			this.logDiagnostic("public review followup dismissed", {
				trigger,
				reviewPromptState,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram public review followup dismissed", {trigger})
		}

		return didHandle
	}

	async handleLowExperienceScore(score, trigger) {
		let userProfile = this.getUserProfile()
		if(!userProfile){
			return false
		}

		let feedbackResult = await feedbackDialog({
			title: "What could we improve?",
			message: "Tell us what was missing, frustrating, or confusing. We read every response.",
			feedbackSubject: "Low Score Feedback",
			feedbackData: {
				source: "genogram_review_flow",
				score,
				trigger,
			},
		})

		let reviewPromptState = this.reviewPromptManager.getState(userProfile)
		let now = Date.now()
		let isFeedbackSubmitted = feedbackResult !== false
		if(isFeedbackSubmitted){
			this.reviewPromptManager.markCompleted(reviewPromptState, {
				now,
				reason: ReviewPromptCompletionReasonEnum.PRIVATE_FEEDBACK_SUBMITTED,
			})
			this.logDiagnostic("private feedback submitted", {
				trigger,
				reviewPromptState,
			})
			await this.persistUserProfile(userProfile)
			this.trackEvent("Genogram private feedback submitted", {
				trigger,
				score,
			})
			return true
		}

		this.reviewPromptManager.markSnoozed(reviewPromptState, now + GENOGRAM_REVIEW_SNOOZE_MS)
		this.logDiagnostic("private feedback dismissed", {
			trigger,
			snoozedUntil: reviewPromptState.snoozedUntil,
		})
		await this.persistUserProfile(userProfile)
		this.trackEvent("Genogram private feedback dismissed", {
			trigger,
			score,
		})
		return true
	}

	getUserProfile() {
		return this.component?.userProfile || this.component?.$authManager?.getUserProfile?.() || null
	}

	canPersistUserProfile(userProfile = this.getUserProfile()) {
		let authManager = this.component?.$authManager
		let persistedUserProfile = authManager?.getUserProfile?.()
		let isLoggedIn = authManager?.isLoggedIn?.()
		let canPersist = !!userProfile?.id && !!persistedUserProfile?.id && !!isLoggedIn
		if(canPersist){
			canPersist = userProfile.id === persistedUserProfile.id
		}
		return canPersist
	}

	async persistUserProfile(userProfile) {
		if(!this.canPersistUserProfile(userProfile)){
			this.logDiagnostic("persistUserProfile skipped: guest/local user", {
				userId: userProfile?.id,
				isLoggedIn: this.component?.$authManager?.isLoggedIn?.(),
			})
			return false
		}
		return this.component.$authManager.updateUserProfile(userProfile)
	}

	trackEvent(eventName, props = {}) {
		this.component?.$analyticManager?.trackEvent?.(eventName, props)
	}

	logDiagnostic(message, payload = {}) {
		console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramReviewFlowManager: ${message}`, payload)
	}
}
